import { useState } from "react";
import { toast } from "sonner";
import { MessageSquare, Pencil, Trash2, Loader2, Send } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { it } from "date-fns/locale";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { useFetch } from "@/lib/hooks";
import { useAuth } from "@/context/AuthContext";
import { ConfirmDialog } from "./ConfirmDialog";

export const CommentBadge = ({ n, testid }) => (n ? (
  <span className="inline-flex items-center gap-0.5 text-[11px] font-bold text-[#0A1128]" title={`${n} commenti`} data-testid={testid}>
    <MessageSquare className="h-3 w-3" />{n}
  </span>
) : null);

export const CommentsThread = ({ tipo, targetId, onChange }) => {
  const { user } = useAuth();
  const { data: items, reload } = useFetch(`/comments?target_tipo=${tipo}&target_id=${targetId}`);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [edit, setEdit] = useState(null);
  const [del, setDel] = useState(null);

  const refresh = () => { reload(); onChange?.(); };
  const add = async () => {
    if (!text.trim()) return;
    setSending(true);
    try { await api.post("/comments", { target_tipo: tipo, target_id: targetId, testo: text }); setText(""); refresh(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setSending(false); }
  };
  const save = async () => {
    if (!edit.testo.trim()) return toast.error("Il commento è vuoto");
    try { await api.put(`/comments/${edit.id}`, { testo: edit.testo }); setEdit(null); reload(); toast.success("Commento modificato"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => {
    try { await api.delete(`/comments/${del.id}`); toast.success("Commento eliminato"); refresh(); }
    catch (e) { toast.error(errMsg(e)); }
    setDel(null);
  };

  return (
    <div className="rounded-2xl border border-[#5FB3EA] bg-[#8FD0FB] p-4" data-testid={`comments-${tipo}`}>
      <div className="font-display font-extrabold text-[#0A1128] flex items-center gap-2 mb-3">
        <MessageSquare className="h-4 w-4 text-[#E8173A]" />Commenti interni <span className="text-xs text-slate-500">({items?.length || 0})</span>
      </div>
      <div className="space-y-3 max-h-[280px] overflow-y-auto core-scroll pr-1" data-testid="comments-list">
        {(items || []).map((c) => {
          const mine = c.user_id === user.id;
          return (
            <div key={c.id} className="flex gap-2.5 group" data-testid={`comment-${c.id}`}>
              <div className="h-8 w-8 shrink-0 rounded-full bg-[#0A1128] text-white text-[11px] font-bold flex items-center justify-center">{c.autore_iniziali}</div>
              <div className="min-w-0 flex-1 rounded-xl bg-[#A6DAFC] px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-xs"><span className="font-bold text-[#0A1128]">{c.autore_nome}</span>
                    <span className="text-slate-500"> · {formatDistanceToNow(new Date(c.created_at), { addSuffix: true, locale: it })}{c.edited ? " · modificato" : ""}</span></div>
                  {mine && edit?.id !== c.id && (
                    <div className="flex gap-0.5 opacity-60 group-hover:opacity-100">
                      <button type="button" onClick={() => setEdit({ id: c.id, testo: c.testo })} className="p-1 rounded hover:bg-[#8FD0FB]" data-testid={`comment-edit-${c.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                      <button type="button" onClick={() => setDel(c)} className="p-1 rounded hover:bg-red-50 text-[#E8173A]" data-testid={`comment-delete-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  )}
                </div>
                {edit?.id === c.id ? (
                  <div className="mt-1.5 space-y-2">
                    <Textarea rows={3} value={edit.testo} onChange={(e) => setEdit({ ...edit, testo: e.target.value })} className="rounded-xl text-sm" data-testid={`comment-edit-input-${c.id}`} />
                    <div className="flex gap-2 justify-end">
                      <Button type="button" size="sm" variant="ghost" className="rounded-full h-7" onClick={() => setEdit(null)} data-testid={`comment-edit-cancel-${c.id}`}>Annulla</Button>
                      <Button type="button" size="sm" className="rounded-full h-7 bg-[#0A1128]" onClick={save} data-testid={`comment-edit-save-${c.id}`}>Salva</Button>
                    </div>
                  </div>
                ) : <div className="text-sm text-[#0A1128] whitespace-pre-wrap break-words mt-0.5" data-testid={`comment-text-${c.id}`}>{c.testo}</div>}
              </div>
            </div>
          );
        })}
        {items && !items.length && <div className="text-xs text-slate-500" data-testid="comments-empty">Nessun commento. Scrivi il primo per aggiornare i soci.</div>}
      </div>
      <div className="mt-3 flex gap-2 items-end">
        <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Scrivi un commento..." className="rounded-xl text-sm"
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); add(); } }} data-testid="comment-input" />
        <Button type="button" onClick={add} disabled={sending || !text.trim()} className="rounded-full bg-[#E8173A] hover:bg-[#C8102E] h-10 w-10 p-0 shrink-0" data-testid="comment-submit">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Eliminare il commento?" description="Il commento verrà eliminato definitivamente." onConfirm={remove} />
    </div>
  );
};
